import { useEffect, useMemo, useRef, useState } from "react";
import { LoaderCircle, MessageCircle, Search, X } from "lucide-react";
import { ListSkeleton } from "../skeletons/ShopperSkeletons";
import {
  formatConversationPreview,
  formatConversationTimestamp,
  getConversationDisplayName,
  getConversationInitials,
  getConversationSearchTokens,
  getConversationUnreadValue,
} from "./messagingUi";

function toConversationKey(value) {
  if (value == null) return "";
  return String(value);
}

function normalizeSearchValue(value) {
  if (value == null) return "";
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function toSearchTerms(value) {
  const normalized = normalizeSearchValue(value);
  if (!normalized) return [];
  return normalized.split(/\s+/).filter(Boolean);
}

function matchesConversationSearch(conversation, actorType, normalizedSearch) {
  if (!normalizedSearch) return true;

  const terms = toSearchTerms(normalizedSearch);
  if (!terms.length) return true;

  const normalizedTokens = getConversationSearchTokens(conversation, actorType)
    .map((value) => normalizeSearchValue(value))
    .filter(Boolean);

  if (!normalizedTokens.length) return false;

  return terms.every((term) => normalizedTokens.some((token) => token.includes(term)));
}

export default function ConversationList({
  conversations = [],
  activeConversationId,
  loading = false,
  onSelect,
  actorType = "SHOPPER",
  getConversationUnreadCount,
  searchPlaceholder,
  loadingLabel,
  emptyTitle,
  emptyDescription,
  emptySearchTitle,
  emptySearchDescription,
  onSearch,
  seamlessScroll = false,
  scrollRegionClassName = "",
}) {
  const [search, setSearch] = useState("");
  const [remoteResults, setRemoteResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const requestIdRef = useRef(0);

  const resolvedSearchPlaceholder = searchPlaceholder || (actorType === "SELLER" ? "Search shoppers..." : "Search sellers...");
  const resolvedLoadingLabel = loadingLabel || "Loading conversations";
  const resolvedEmptyTitle = emptyTitle || "No conversations";
  const resolvedEmptyDescription = emptyDescription || "Messages will appear here once a conversation starts.";
  const resolvedEmptySearchTitle = emptySearchTitle || "No results";
  const resolvedEmptySearchDescription = emptySearchDescription || "Try a different name or keyword.";
  const normalizedSearch = normalizeSearchValue(search);
  const activeConversationKey = toConversationKey(activeConversationId);

  const filtered = useMemo(() => {
    if (!normalizedSearch) return conversations;
    return conversations.filter((conversation) => matchesConversationSearch(conversation, actorType, normalizedSearch));
  }, [actorType, conversations, normalizedSearch]);

  useEffect(() => {
    if (!normalizedSearch || typeof onSearch !== "function") {
      requestIdRef.current += 1;
      setRemoteResults(null);
      setSearchLoading(false);
      setSearchError("");
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setSearchLoading(true);
    setSearchError("");

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await onSearch(search.trim());
        if (requestIdRef.current !== requestId) return;
        setRemoteResults(Array.isArray(results) ? results : []);
      } catch {
        if (requestIdRef.current !== requestId) return;
        setSearchError("Unable to search conversations right now.");
        setRemoteResults(null);
      } finally {
        if (requestIdRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    }, 220);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [normalizedSearch, onSearch, search]);

  const conversationsById = useMemo(() => {
    const map = new Map();
    conversations.forEach((conversation) => {
      map.set(conversation.id, conversation);
    });
    return map;
  }, [conversations]);

  const visibleConversations = useMemo(() => {
    if (!normalizedSearch) {
      return conversations;
    }

    if (!remoteResults) {
      return filtered;
    }

    const merged = [
      ...filtered,
      ...remoteResults.map((conversation) => (conversationsById.get(conversation.id) || conversation)),
    ];

    const deduped = [];
    const seen = new Set();

    merged.forEach((conversation) => {
      const key = toConversationKey(conversation?.id);
      if (!key || seen.has(key)) return;
      seen.add(key);
      deduped.push(conversation);
    });

    return deduped.filter((conversation) => matchesConversationSearch(conversation, actorType, normalizedSearch));
  }, [actorType, conversations, conversationsById, filtered, normalizedSearch, remoteResults]);

  const containerClassName = seamlessScroll
    ? "h-full min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain"
    : "h-full min-h-0 flex flex-1 flex-col overflow-hidden";
  const searchHeaderClassName = seamlessScroll
    ? "sticky top-0 z-10 border-b border-neutral-100 bg-white px-3 pb-2.5 pt-3 dark:border-neutral-800 dark:bg-neutral-900 md:px-3.5 md:pb-3"
    : "shrink-0 border-b border-neutral-100 bg-white px-3 pb-2.5 pt-3 dark:border-neutral-800 dark:bg-neutral-900 md:px-3.5 md:pb-3";
  const listClassName = seamlessScroll
    ? scrollRegionClassName
    : `min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain ${scrollRegionClassName}`.trim();

  return (
    <div className={containerClassName}>
      <div className={searchHeaderClassName}>
        <div className="relative">
          <Search
            size={13}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={resolvedSearchPlaceholder}
            className="w-full rounded-2xl border border-neutral-200 bg-neutral-50 py-2.5 pl-9 pr-16 text-[13px] text-neutral-900 outline-none transition focus:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:focus:border-neutral-500"
          />

          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
            {searchLoading ? (
              <LoaderCircle size={14} className="animate-spin text-neutral-400" />
            ) : null}

            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition hover:bg-neutral-200/70 hover:text-neutral-700 dark:hover:bg-neutral-700/70 dark:hover:text-neutral-200"
                aria-label="Clear conversation search"
              >
                <X size={13} />
              </button>
            ) : null}
          </div>
        </div>

        {searchError ? (
          <p className="mt-2 text-[11px] text-rose-500 dark:text-rose-300">
            {searchError}
          </p>
        ) : null}
      </div>

      <div className={listClassName}>
        {loading && conversations.length === 0 && (
          <div className="p-3"><ListSkeleton rows={4} label={resolvedLoadingLabel} /></div>
        )}

        {!loading && visibleConversations.length === 0 && !searchLoading && (
          <div className="flex h-32 flex-col items-center justify-center px-4 text-center text-neutral-400">
            <MessageCircle size={24} className="mb-2 opacity-40" />
            <p className="text-[12px] font-medium">{normalizedSearch ? resolvedEmptySearchTitle : resolvedEmptyTitle}</p>
            <p className="mt-1 text-[11px] opacity-75">
              {normalizedSearch ? resolvedEmptySearchDescription : resolvedEmptyDescription}
            </p>
          </div>
        )}

        {visibleConversations.map((conversation) => {
          const conversationKey = toConversationKey(conversation.id);
          const active = conversationKey === activeConversationKey;
          const unread = Number(getConversationUnreadCount?.(conversation) ?? getConversationUnreadValue(conversation, actorType));
          const displayName = getConversationDisplayName(conversation, actorType);
          const preview = formatConversationPreview(conversation);
          const timeLabel = formatConversationTimestamp(conversation.last_message_at || conversation.created_at);

          return (
            <button
              key={conversationKey}
              type="button"
              onClick={() => onSelect(conversation.id)}
              className={`w-full border-b border-neutral-100 px-3 py-3 text-left transition dark:border-neutral-800/60 md:px-3.5 md:py-3.5 ${
                active
                  ? "bg-neutral-100 dark:bg-neutral-800"
                  : "hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
              }`}
              aria-current={active ? "page" : undefined}
            >
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-200 text-sm font-bold text-neutral-500 dark:bg-neutral-700 md:h-11 md:w-11">
                  {getConversationInitials(conversation, actorType)}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={`block truncate pr-1 text-[13px] leading-tight ${
                        unread > 0
                          ? "font-bold text-neutral-900 dark:text-white"
                          : "font-semibold text-neutral-800 dark:text-neutral-100"
                      }`}
                    >
                      {displayName}
                    </span>

                    <div className="flex shrink-0 items-center gap-1.5 pl-2">
                      {unread > 0 && (
                        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                          {unread > 99 ? "99+" : unread}
                        </span>
                      )}
                      <span className="text-[10px] text-neutral-400">
                        {timeLabel}
                      </span>
                    </div>
                  </div>

                  <p
                    className={`mt-1 truncate text-[11px] leading-5 ${
                      unread > 0
                        ? "font-medium text-neutral-700 dark:text-neutral-200"
                        : "text-neutral-400 dark:text-neutral-500"
                    }`}
                  >
                    {preview}
                  </p>

                  {normalizedSearch && displayName && (
                    <p className="mt-0.5 truncate text-[10px] uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">
                      {displayName}
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
